import React, { useState, useEffect, useCallback, useRef } from 'react';
import { theme } from '../theme';
import Icons from './Icons';
import { apiGet } from '../api';

const PAGE_SIZE = 50;

const MODULE_LABELS = {
  HCM_Person: 'HCM Person',
  HCM_HR:     'HCM HR',
  HCM_Pay:    'HCM Payroll',
  Finance_AP: 'AP',
  Finance_AR: 'AR',
  Finance_GL: 'GL',
  Finance_PO: 'PO',
  Finance_FA: 'Fixed Assets',
  Projects:   'Projects',
  Inventory:  'Inventory',
  SCM:        'SCM',
  Suppliers:  'Suppliers',
};

// ── Family grouping helpers ──────────────────────────────────────────────────
const SUFFIXES = ['_VL_SEC','_U_SEC','_SEC','_TL','_VL','_GT','_INT','_ALL','_F','_B','_V','_X','_M'];

const SUFFIX_META = {
  _B:      { badge: 'BASE',  tip: 'Language-independent base table',                          color: '#a78bfa' },
  _TL:     { badge: 'TL',    tip: "Translated strings — join on LANGUAGE = USERENV('LANG')",  color: '#f59e0b' },
  _VL:     { badge: 'VL',    tip: 'Combined base+translation view — use this for queries',    color: '#22c55e' },
  _F:      { badge: 'DATE',  tip: 'Date-tracked — filter EFFECTIVE_START_DATE/END_DATE',      color: '#38bdf8' },
  _SEC:    { badge: 'SEC',   tip: 'Security view — row-level access control applied',         color: '#ef4444' },
  _U_SEC:  { badge: 'SEC',   tip: 'Update security view — row-level access control applied',  color: '#ef4444' },
  _VL_SEC: { badge: 'SEC',   tip: 'Security variant of VL view',                             color: '#ef4444' },
  _GT:     { badge: 'TEMP',  tip: 'Global temporary — session-scoped staging',               color: '#6b7280' },
  _ALL:    { badge: 'ALL',   tip: 'Cross-org — filter ORG_ID if needed',                     color: '#8b5cf6' },
  _INT:    { badge: 'IFACE', tip: 'Interface/import table',                                  color: '#f97316' },
  _V:      { badge: 'VIEW',  tip: 'Standard reporting view',                                  color: theme.accent },
};

function getStem(name) {
  for (const s of SUFFIXES) {
    if (name.endsWith(s)) return { stem: name.slice(0, -s.length), suffix: s };
  }
  return { stem: name, suffix: null };
}

function groupObjects(objects) {
  const map = {};
  for (const obj of objects) {
    const { stem } = getStem(obj.name);
    if (!map[stem]) map[stem] = [];
    map[stem].push(obj);
  }
  // Sort stems alphabetically; within each group sort by suffix order
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([stem, members]) => ({ stem, members: members.sort((a, b) => {
      const ai = SUFFIXES.indexOf(getStem(a.name).suffix);
      const bi = SUFFIXES.indexOf(getStem(b.name).suffix);
      return ai - bi;
    })}));
}

// ── UI helpers ───────────────────────────────────────────────────────────────
function highlight(text, q) {
  if (!q || !text) return text;
  const idx = text.toUpperCase().indexOf(q.toUpperCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: `${theme.accent}44`, color: theme.text, borderRadius: '2px' }}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function TypeBadge({ type }) {
  const isView = type === 'VIEW';
  return (
    <span style={{
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em',
      padding: '1px 5px', borderRadius: '3px',
      background: isView ? `${theme.warning}22` : `${theme.accent}22`,
      color: isView ? theme.warning : theme.accent, flexShrink: 0,
    }}>{type}</span>
  );
}

function SuffixBadge({ suffix }) {
  const meta = SUFFIX_META[suffix];
  if (!meta) return null;
  return (
    <span title={meta.tip} style={{
      fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
      padding: '1px 5px', borderRadius: '3px', flexShrink: 0,
      background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`,
    }}>{meta.badge}</span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DataDictionary({ onClose, onOpenInEditor }) {
  const [sddIndex,      setSddIndex]      = useState(null);
  const [activeModule,  setActiveModule]  = useState(null);
  const [moduleObjects, setModuleObjects] = useState([]);
  const [selectedObj,   setSelectedObj]   = useState(null);
  const [loadingModule, setLoadingModule] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search,        setSearch]        = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [page,          setPage]          = useState(1);
  const [groupByFamily, setGroupByFamily] = useState(true);
  // Join columns view
  const [view,            setView]            = useState('objects'); // 'objects' | 'joinColumns'
  const [topColumns,      setTopColumns]      = useState(null);
  const [loadingCols,     setLoadingCols]     = useState(false);
  const [selectedColumn,  setSelectedColumn]  = useState(null);
  const [columnTables,    setColumnTables]    = useState(null);
  const [loadingColTbls,  setLoadingColTbls]  = useState(false);
  const [colFilter,       setColFilter]       = useState('');
  // Family expand/collapse
  const [collapsedStems, setCollapsedStems]   = useState({});

  const searchTimer = useRef(null);

  useEffect(() => {
    apiGet('/sdd/index').then(setSddIndex).catch(console.error);
  }, []);

  const loadModule = useCallback(async (mod) => {
    // Clicking the active pill deselects it
    if (mod === activeModule && view === 'objects') {
      setActiveModule(null);
      setModuleObjects([]);
      setSelectedObj(null);
      setSearchResults(null);
      setSearch('');
      return;
    }
    setView('objects');
    setActiveModule(mod);
    setSelectedObj(null);
    setSearchResults(null);
    setSearch('');
    setPage(1);
    setCollapsedStems({});
    setLoadingModule(true);
    try {
      const data = await apiGet(`/sdd/${mod}`);
      setModuleObjects(data.objects || []);
    } catch (e) { console.error(e); }
    finally { setLoadingModule(false); }
  }, [activeModule, view]);

  const loadObject = useCallback(async (mod, name) => {
    setLoadingDetail(true);
    try {
      const data = await apiGet(`/sdd/${mod}/object/${encodeURIComponent(name)}`);
      setSelectedObj(data.object || null);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  }, []);

  const loadJoinColumns = useCallback(async () => {
    setView('joinColumns');
    setActiveModule(null);
    setSelectedObj(null);
    setSelectedColumn(null);
    setColumnTables(null);
    if (topColumns) return; // already loaded
    setLoadingCols(true);
    try {
      const data = await apiGet('/sdd/columns/top?limit=100');
      setTopColumns(data.columns || []);
    } catch (e) { console.error(e); }
    finally { setLoadingCols(false); }
  }, [topColumns]);

  const loadColumnTables = useCallback(async (colName) => {
    setSelectedColumn(colName);
    setLoadingColTbls(true);
    try {
      const data = await apiGet(`/sdd/columns/${encodeURIComponent(colName)}/tables`);
      setColumnTables(data);
    } catch (e) { console.error(e); }
    finally { setLoadingColTbls(false); }
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    if (!val.trim() || val.trim().length < 2) { setSearchResults(null); setPage(1); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const mod = activeModule || 'all';
        const data = await apiGet(`/sdd/search?q=${encodeURIComponent(val)}&module=${mod}`);
        setSearchResults(data.results || []);
        setPage(1);
      } catch (e) { console.error(e); }
    }, 300);
  };

  const openInEditor = (obj) => {
    if (!obj) return;
    const alias = obj.name.split('_').map(w => w[0]).join('').toLowerCase();
    const cols  = (obj.columns || []).slice(0, 10).map(c => `       ${alias}.${c.name}`).join(',\n');
    const sql   = `SELECT ${cols || `       ${alias}.*`}\nFROM   FUSION.${obj.name} ${alias}\nWHERE  ROWNUM <= 100`;
    onOpenInEditor(sql, obj.name);
    onClose();
  };

  const toggleStem = (stem) => setCollapsedStems(p => ({ ...p, [stem]: !p[stem] }));

  // ── Derived list ─────────────────────────────────────────────────────────
  const displayList   = searchResults !== null ? searchResults : moduleObjects;
  const totalPages    = Math.ceil(displayList.length / PAGE_SIZE);
  const pagedList     = displayList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const grouped       = groupByFamily && searchResults === null ? groupObjects(pagedList) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme.bg }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 16px', borderBottom: `1px solid ${theme.border}`,
        background: theme.surface, flexShrink: 0,
      }}>
        <Icons.BookOpen />
        <span style={{ fontWeight: 700, fontSize: '14px' }}>Data Dictionary</span>
        {sddIndex && <span style={{ fontSize: '12px', color: theme.textMuted }}>FUSION — {Object.keys(sddIndex.modules || {}).length} modules</span>}
        <div style={{ flex: 1, maxWidth: '400px', position: 'relative', marginLeft: '8px' }}>
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder={activeModule ? `Search in ${MODULE_LABELS[activeModule] || activeModule}…` : 'Search all modules…'}
            style={{
              width: '100%', padding: '6px 28px 6px 10px',
              background: theme.codeBg, border: `1px solid ${theme.border}`,
              borderRadius: '6px', color: theme.text, fontSize: '12px',
              outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={e => { e.target.style.borderColor = theme.accent; }}
            onBlur={e => { e.target.style.borderColor = theme.border; }}
          />
          {search && (
            <button onClick={() => handleSearch('')} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '2px' }}>
              <Icons.X style={{ width: 11, height: 11 }} />
            </button>
          )}
        </div>
        <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '4px', marginLeft: 'auto' }}>
          <Icons.X />
        </button>
      </div>

      {/* Module pills + Join Columns tab */}
      <div style={{
        display: 'flex', gap: '4px', padding: '8px 16px', flexWrap: 'wrap', alignItems: 'center',
        borderBottom: `1px solid ${theme.border}`, flexShrink: 0, background: theme.surface,
      }}>
        {/* Join Columns tab */}
        <button
          onClick={loadJoinColumns}
          style={{
            padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
            border: `1px solid ${view === 'joinColumns' ? theme.success : theme.border}`,
            background: view === 'joinColumns' ? `${theme.success}22` : 'transparent',
            color: view === 'joinColumns' ? theme.success : theme.textMuted,
            cursor: 'pointer', fontWeight: view === 'joinColumns' ? 600 : 400,
            display: 'flex', alignItems: 'center', gap: '5px',
          }}
        >
          🔗 Join Columns
        </button>
        <span style={{ color: theme.border, fontSize: '16px', lineHeight: 1 }}>|</span>
        {sddIndex ? Object.entries(sddIndex.modules || {}).map(([mod, stats]) => (
          <button key={mod} onClick={() => loadModule(mod)} style={{
            padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
            border: `1px solid ${activeModule === mod && view === 'objects' ? theme.accent : theme.border}`,
            background: activeModule === mod && view === 'objects' ? `${theme.accent}22` : 'transparent',
            color: activeModule === mod && view === 'objects' ? theme.accent : theme.textMuted,
            cursor: 'pointer', fontWeight: activeModule === mod && view === 'objects' ? 600 : 400,
          }}>
            {MODULE_LABELS[mod] || mod}
            {stats.objectCount ? <span style={{ opacity: 0.6, marginLeft: '4px' }}>{stats.objectCount}</span> : null}
          </button>
        )) : <span style={{ fontSize: '12px', color: theme.textMuted }}>Loading…</span>}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── JOIN COLUMNS VIEW ─────────────────────────────────────────── */}
        {view === 'joinColumns' && (
          <>
            {/* Column list */}
            <div style={{ width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
                <input
                  value={colFilter}
                  onChange={e => setColFilter(e.target.value)}
                  placeholder="Filter columns…"
                  style={{ width: '100%', padding: '5px 8px', background: theme.codeBg, border: `1px solid ${theme.border}`, borderRadius: '5px', color: theme.text, fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loadingCols && <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>Loading…</div>}
                {!loadingCols && (topColumns || [])
                  .filter(c => !colFilter || c.column.toUpperCase().includes(colFilter.toUpperCase()))
                  .map(col => {
                    const isSelected = selectedColumn === col.column;
                    return (
                      <div key={col.column} onClick={() => loadColumnTables(col.column)}
                        style={{
                          padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                          background: isSelected ? `${theme.accent}18` : 'transparent',
                          borderLeft: isSelected ? `2px solid ${theme.accent}` : '2px solid transparent',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = theme.surfaceHover; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px', fontWeight: 500, color: isSelected ? theme.accent : theme.text }}>
                          {highlight(col.column, colFilter)}
                        </span>
                        <span style={{ fontSize: '10px', color: theme.accent, flexShrink: 0 }}>{col.dataType}</span>
                        <span style={{ fontSize: '10px', color: theme.textMuted, flexShrink: 0 }}>{col.count} tables</span>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Column drill-down OR object detail */}
            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              {/* Object detail panel (when Detail button is clicked) */}
              {(selectedObj || loadingDetail) && (
                <>
                  {selectedObj && (
                    <button
                      onClick={() => setSelectedObj(null)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '16px', background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', fontSize: '12px', padding: 0 }}
                      onMouseEnter={e => e.currentTarget.style.color = theme.text}
                      onMouseLeave={e => e.currentTarget.style.color = theme.textMuted}
                    >
                      ← Back to {selectedColumn}
                    </button>
                  )}
                  {loadingDetail
                    ? <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '40px', textAlign: 'center' }}>Loading…</div>
                    : <ObjectDetail obj={selectedObj} onOpenInEditor={openInEditor} />
                  }
                </>
              )}
              {/* Column tables drill-down */}
              {!selectedObj && !loadingDetail && !selectedColumn && (
                <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '40px', textAlign: 'center' }}>
                  Select a column to see all tables that contain it.
                </div>
              )}
              {!selectedObj && !loadingDetail && selectedColumn && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 700 }}>{selectedColumn}</span>
                      {columnTables && <span style={{ fontSize: '12px', color: theme.textMuted }}>{columnTables.dataType} · {columnTables.count} tables</span>}
                    </div>
                  </div>
                  {loadingColTbls && <div style={{ color: theme.textMuted, fontSize: '12px' }}>Loading…</div>}
                  {!loadingColTbls && columnTables && Object.entries(columnTables.byModule).map(([mod, tables]) => (
                    <div key={mod} style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
                        {MODULE_LABELS[mod] || mod} ({tables.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        {tables.map(t => (
                          <ColumnTableRow key={t.objectName} t={t} onOpen={() => loadObject(mod, t.objectName)} onOpenInEditor={() => {
                            apiGet(`/sdd/${mod}/object/${encodeURIComponent(t.objectName)}`).then(data => {
                              if (data.object) openInEditor(data.object);
                            });
                          }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {/* ── OBJECTS VIEW ─────────────────────────────────────────────── */}
        {view === 'objects' && (
          <>
            {/* Object list */}
            <div style={{ width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.border}`, overflow: 'hidden' }}>
              {/* List header */}
              <div style={{
                padding: '6px 12px', borderBottom: `1px solid ${theme.border}`,
                fontSize: '11px', color: theme.textMuted, flexShrink: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>
                  {searchResults !== null ? `${searchResults.length} results`
                    : activeModule ? `${moduleObjects.length} objects` : 'Select a module above'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {totalPages > 1 && <span>p{page}/{totalPages}</span>}
                  {activeModule && searchResults === null && (
                    <button
                      onClick={() => setGroupByFamily(v => !v)}
                      title={groupByFamily ? 'Disable family grouping' : 'Enable family grouping'}
                      style={{
                        background: groupByFamily ? `${theme.accent}22` : 'none',
                        border: `1px solid ${groupByFamily ? theme.accent : theme.border}`,
                        color: groupByFamily ? theme.accent : theme.textMuted,
                        cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                      }}
                    >
                      Family
                    </button>
                  )}
                </div>
              </div>

              {/* Rows */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loadingModule && <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>Loading…</div>}
                {!loadingModule && !activeModule && searchResults === null && (
                  <div style={{ padding: '24px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>Select a module to browse objects.</div>
                )}

                {/* Grouped view */}
                {!loadingModule && grouped && grouped.map(({ stem, members }) => {
                  const isGroup = members.length > 1;
                  if (!isGroup) {
                    const obj = members[0];
                    return <ObjectRow key={obj.name} obj={obj} search={search} selectedName={selectedObj?.name} activeModule={activeModule} onSelect={loadObject} />;
                  }
                  const collapsed = collapsedStems[stem];
                  return (
                    <div key={stem}>
                      {/* Family header */}
                      <div
                        onClick={() => toggleStem(stem)}
                        style={{
                          padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                          background: theme.surface, borderBottom: `1px solid ${theme.border}44`,
                          fontSize: '11px', fontWeight: 700, color: theme.textMuted, userSelect: 'none',
                        }}
                      >
                        <span style={{ fontSize: '9px', opacity: 0.7 }}>{collapsed ? '▶' : '▼'}</span>
                        <span style={{ fontFamily: 'monospace', flex: 1 }}>{stem}</span>
                        <span style={{ opacity: 0.6 }}>{members.length}</span>
                      </div>
                      {!collapsed && members.map(obj => (
                        <ObjectRow key={obj.name} obj={obj} search={search} selectedName={selectedObj?.name} activeModule={activeModule} onSelect={loadObject} indent />
                      ))}
                    </div>
                  );
                })}

                {/* Flat view (search results or grouping off) */}
                {!loadingModule && !grouped && pagedList.map(item => {
                  const name = item.name || item.objectName;
                  const type = item.type || item.objectType;
                  const mod  = item.module || activeModule;
                  const isSelected = selectedObj?.name === name;
                  return (
                    <div key={`${mod}-${name}`} onClick={() => loadObject(mod, name)}
                      style={{
                        padding: '7px 12px', cursor: 'pointer',
                        background: isSelected ? `${theme.accent}18` : 'transparent',
                        borderLeft: isSelected ? `2px solid ${theme.accent}` : '2px solid transparent',
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = theme.surfaceHover; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TypeBadge type={type} />
                        <span style={{ fontSize: '12px', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isSelected ? theme.accent : theme.text }}>
                          {highlight(name, search)}
                        </span>
                        {item.columnCount != null && <span style={{ fontSize: '10px', color: theme.textMuted, flexShrink: 0 }}>{item.columnCount}c</span>}
                      </div>
                      {item.matchType === 'column' && (
                        <div style={{ fontSize: '10px', color: theme.textMuted, marginTop: '2px', paddingLeft: '2px' }}>
                          col: {highlight(item.matchValue, search)} <span style={{ opacity: 0.6 }}>{item.columnType}</span>
                        </div>
                      )}
                      {searchResults !== null && item.module && (
                        <div style={{ fontSize: '10px', color: theme.accent, opacity: 0.7, marginTop: '1px' }}>{MODULE_LABELS[item.module] || item.module}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', padding: '8px', borderTop: `1px solid ${theme.border}`, flexShrink: 0 }}>
                  <PagBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</PagBtn>
                  {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                    let p;
                    if (totalPages <= 7) p = i + 1;
                    else if (page <= 4) p = i + 1;
                    else if (page >= totalPages - 3) p = totalPages - 6 + i;
                    else p = page - 3 + i;
                    return <PagBtn key={p} onClick={() => setPage(p)} active={p === page}>{p}</PagBtn>;
                  })}
                  <PagBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</PagBtn>
                </div>
              )}
            </div>

            {/* Object detail */}
            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              {loadingDetail && <div style={{ color: theme.textMuted, fontSize: '13px' }}>Loading…</div>}
              {!loadingDetail && !selectedObj && <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '40px', textAlign: 'center' }}>Select an object from the list to view its details.</div>}
              {!loadingDetail && selectedObj && <ObjectDetail obj={selectedObj} onOpenInEditor={openInEditor} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── ObjectRow ─────────────────────────────────────────────────────────────────
function ObjectRow({ obj, search, selectedName, activeModule, onSelect, indent }) {
  const isSelected = selectedName === obj.name;
  const { suffix } = getStem(obj.name);
  return (
    <div
      onClick={() => onSelect(activeModule, obj.name)}
      style={{
        padding: indent ? '5px 12px 5px 20px' : '7px 12px', cursor: 'pointer',
        background: isSelected ? `${theme.accent}18` : 'transparent',
        borderLeft: isSelected ? `2px solid ${theme.accent}` : '2px solid transparent',
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = theme.surfaceHover; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {suffix && <SuffixBadge suffix={suffix} />}
        {!suffix && <TypeBadge type={obj.type} />}
        <span style={{ fontSize: '12px', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isSelected ? theme.accent : theme.text }}>
          {highlight(obj.name, search)}
        </span>
        <span style={{ fontSize: '10px', color: theme.textMuted, flexShrink: 0 }}>{obj.columnCount}c</span>
      </div>
    </div>
  );
}

// ── ColumnTableRow ────────────────────────────────────────────────────────────
function ColumnTableRow({ t, onOpen, onOpenInEditor }) {
  const { suffix } = getStem(t.objectName);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', borderRadius: '5px', background: theme.surface, border: `1px solid ${theme.border}` }}>
      {suffix ? <SuffixBadge suffix={suffix} /> : <TypeBadge type={t.objectType} />}
      <span style={{ fontFamily: 'monospace', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.objectName}</span>
      <button onClick={onOpen} title="View detail" style={{ background: 'none', border: `1px solid ${theme.border}`, color: theme.textMuted, cursor: 'pointer', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>Detail</button>
      <button onClick={onOpenInEditor} title="Open in editor" style={{ background: `${theme.accent}22`, border: `1px solid ${theme.accent}55`, color: theme.accent, cursor: 'pointer', padding: '2px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 600 }}>Open</button>
    </div>
  );
}

// ── PagBtn ────────────────────────────────────────────────────────────────────
function PagBtn({ children, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: active ? theme.accent : 'none',
      border: `1px solid ${active ? theme.accent : theme.border}`,
      color: active ? '#fff' : theme.textMuted,
      cursor: disabled ? 'default' : 'pointer',
      padding: '3px 7px', borderRadius: '4px', fontSize: '11px',
      opacity: disabled ? 0.4 : 1,
    }}>{children}</button>
  );
}

// ── Object Detail Panel ───────────────────────────────────────────────────────
function ObjectDetail({ obj, onOpenInEditor }) {
  const [colSearch, setColSearch] = useState('');
  const [sortCol, setSortCol]     = useState(null);
  const [sortDir, setSortDir]     = useState('asc');

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  let columns = obj.columns || [];
  if (colSearch) columns = columns.filter(c => c.name.toUpperCase().includes(colSearch.toUpperCase()) || (c.comment || '').toUpperCase().includes(colSearch.toUpperCase()));
  if (sortCol) {
    columns = [...columns].sort((a, b) => {
      const av = sortCol === 'name' ? a.name : a.dataType;
      const bv = sortCol === 'name' ? b.name : b.dataType;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }

  const { suffix } = getStem(obj.name);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
          {suffix ? <SuffixBadge suffix={suffix} /> : <TypeBadge type={obj.type} />}
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, fontFamily: 'monospace' }}>{obj.name}</h2>
          <button onClick={() => onOpenInEditor(obj)}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '5px', fontSize: '12px', background: `${theme.accent}22`, border: `1px solid ${theme.accent}55`, color: theme.accent, cursor: 'pointer', marginLeft: 'auto' }}
            onMouseEnter={e => e.currentTarget.style.background = `${theme.accent}44`}
            onMouseLeave={e => e.currentTarget.style.background = `${theme.accent}22`}
          >
            <Icons.Play style={{ width: 11, height: 11 }} /> Open in Editor
          </button>
        </div>
        {suffix && SUFFIX_META[suffix] && (
          <div style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '4px', padding: '4px 8px', background: `${theme.surface}`, borderRadius: '4px', border: `1px solid ${theme.border}` }}>
            ℹ️ {SUFFIX_META[suffix].tip}
          </div>
        )}
        {obj.comment && <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted, lineHeight: 1.5 }}>{obj.comment}</p>}
      </div>

      {obj.primaryKey && obj.primaryKey.length > 0 && (
        <div>
          <SectionTitle>Primary Key</SectionTitle>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
            {obj.primaryKey.map(col => (
              <span key={col} style={{ fontFamily: 'monospace', fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: `${theme.success}22`, color: theme.success, border: `1px solid ${theme.success}44` }}>
                🔑 {col}
              </span>
            ))}
          </div>
        </div>
      )}

      {obj.foreignKeys && obj.foreignKeys.length > 0 && (
        <div>
          <SectionTitle>Foreign Keys ({obj.foreignKeys.length})</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
            {obj.foreignKeys.map((fk, i) => (
              <div key={i} style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '5px', background: theme.surface, border: `1px solid ${theme.border}`, fontFamily: 'monospace' }}>
                <span style={{ color: theme.text }}>{fk.columns.join(', ')}</span>
                <span style={{ color: theme.textMuted, margin: '0 8px' }}>→</span>
                <span style={{ color: theme.accent }}>{fk.referencedTable}</span>
                <span style={{ color: theme.textMuted }}>({fk.referencedColumns.join(', ')})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <SectionTitle>Columns ({obj.columns?.length || 0})</SectionTitle>
          <input value={colSearch} onChange={e => setColSearch(e.target.value)} placeholder="Filter columns…"
            style={{ padding: '4px 8px', background: theme.codeBg, border: `1px solid ${theme.border}`, borderRadius: '5px', color: theme.text, fontSize: '11px', outline: 'none', width: '180px' }} />
        </div>
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: '6px', overflow: 'hidden', fontSize: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 60px 1fr', padding: '6px 12px', background: theme.surface, borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, fontWeight: 600, fontSize: '11px' }}>
            <HeaderCell label="COLUMN" col="name" sort={sortCol} dir={sortDir} onSort={toggleSort} />
            <HeaderCell label="TYPE"   col="type" sort={sortCol} dir={sortDir} onSort={toggleSort} />
            <span>NULL</span>
            <span>COMMENT</span>
          </div>
          {columns.length === 0 && <div style={{ padding: '12px', color: theme.textMuted, textAlign: 'center' }}>No columns match filter.</div>}
          {columns.map((col, i) => (
            <div key={col.name} style={{
              display: 'grid', gridTemplateColumns: '2fr 1.2fr 60px 1fr',
              padding: '5px 12px', fontFamily: 'monospace',
              background: i % 2 === 0 ? 'transparent' : `${theme.surface}88`,
              borderBottom: i < columns.length - 1 ? `1px solid ${theme.border}44` : 'none',
              alignItems: 'start',
            }}>
              <span style={{ color: (obj.primaryKey || []).includes(col.name) ? theme.success : theme.text, fontWeight: (obj.primaryKey || []).includes(col.name) ? 700 : 400 }}>
                {highlight(col.name, colSearch)}
                {(obj.primaryKey || []).includes(col.name) && <span style={{ marginLeft: '4px', fontSize: '10px' }}>🔑</span>}
              </span>
              <span style={{ color: theme.accent }}>{col.dataType}{col.length && col.dataType === 'VARCHAR2' ? `(${col.length})` : ''}{col.precision ? `(${col.precision}${col.scale ? ','+col.scale : ''})` : ''}</span>
              <span style={{ color: col.nullable ? theme.textMuted : theme.danger }}>{col.nullable ? 'Y' : 'N'}</span>
              <span style={{ color: theme.textMuted, fontFamily: 'inherit', fontSize: '11px', lineHeight: 1.4 }}>{col.comment || ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{children}</h3>;
}

function HeaderCell({ label, col, sort, dir, onSort }) {
  const active = sort === col;
  return (
    <span onClick={() => onSort(col)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', userSelect: 'none', color: active ? theme.accent : theme.textMuted }}>
      {label}{active ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </span>
  );
}
